import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isClosedOrClosingAccount } from '@wavvest/shared-utils';
import { AssetMap } from '@/util/planning-util';
import {
  ApexUserInformation,
  validateOccupation,
  validateEmployer,
  isValidTrustedContact,
} from '@/util/account-openning-util';
import { ApexAccount, WealthChartData } from '@/types/wealth';
import {
  emptyWealthDataCache,
  invalidateWealthData,
  type WealthDataCache,
} from '@/store/features/wealth-data-cache';
import {
  APEX_STALE_USER_REQUEST,
  fetchApexAccounts,
  fetchApexAssetMap,
  fetchAccountAllocation,
  fetchApexChartData,
  fetchAccountHoldings,
  fetchAccountTransfers,
  fetchAccountFinancialDetails,
  // fetchAccountDetails,
} from './apex-thunks';

type RawHolding = { value_usd?: number | string; amount?: number | string } & Record<
  string,
  unknown
>;

// Drops empty entries and avoids NaN/Infinity prices when a position has no shares.
const withUnitPrices = (holdings: unknown): NonNullable<ApexAccount['holdings']> =>
  (Array.isArray(holdings) ? (holdings as (RawHolding | null | undefined)[]) : [])
    .filter((holding): holding is RawHolding => Boolean(holding))
    .map((holding) => {
      const amount = Number(holding.amount);
      const value = Number(holding.value_usd);
      return {
        ...holding,
        stock_unit_price: amount && Number.isFinite(value) ? value / amount : 0,
      };
    }) as unknown as NonNullable<ApexAccount['holdings']>;

export type ApexState = {
  // base account information
  accounts: ApexAccount[];
  // Holdings for firm-data-blended custodian (Apex) accounts, keyed by
  // custodian_account_id. Populated by the same fetchAccountHoldings thunk
  // used by the legacy per-account pipeline, but independent of `accounts`.
  // Wealth does not fetch/render/total `accounts` for firm-data firms (see
  // shouldFetchLegacyApex) so Apex is not
  // double-counted. The open-account wizard may still populate `accounts`
  // for those firms — fo-apex account_investment_type is the source of truth
  // for disabling already-created goals.
  custodianHoldingsById: Record<string, ApexAccount['holdings']>;
  // Accounts whose last holdings request failed, so the details panel can say
  // so instead of waiting forever.
  holdingsFailedById: Record<string, boolean>;
  // Transfers for the same accounts, kept for the same reason: a firm-data
  // firm rebuilds its accounts outside this slice, so a transfer list written
  // only onto `accounts` would never reach the account the investor expanded.
  custodianTransfersById: Record<string, ApexAccount['transferList']>;
  // chart data
  chartData: WealthChartData;
  // user information
  userInfo: ApexUserInformation;
  assetMap?: AssetMap;
  submissionState?:
    | {
        kind: 'pending';
      }
    | {
        kind: 'failed' | 'success';
        message: string;
      };
  loading: {
    accounts: boolean;
    details: boolean;
    financialDetails: boolean;
    assetMap: boolean;
    chart: boolean;
    allocation: boolean;
    holdings: boolean;
    transfers: boolean;
  };
  error: string | null;
  // Every account id the last load returned (closed ones too, for chart history).
  cache: WealthDataCache & { accountIds: string[] };
};

const getDefaultApexState = (): ApexState => ({
  accounts: [],
  custodianHoldingsById: {},
  holdingsFailedById: {},
  custodianTransfersById: {},
  chartData: {
    values: [],
    timestamps: [],
  },
  userInfo: {
    personalAddress: {
      line1: '',
      line2: '',
      line3: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
    },
    suffix: 'NAME_SUFFIX_UNSPECIFIED',
    taxIdType: 'TAX_ID_TYPE_SSN',
    usITINStatus: 'PASSING',
    countryOfCitizenship: 'US',
    isLargeTrader: false,
    isAdvisor: undefined,
    isInstitutionalCustomer: undefined,
    isCorrespondentEmployee: undefined,
  },
  loading: {
    accounts: false,
    details: false,
    financialDetails: false,
    assetMap: false,
    chart: false,
    allocation: false,
    holdings: false,
    transfers: false,
  },
  error: null,
  cache: { ...emptyWealthDataCache(), accountIds: [] },
});

const initialState: ApexState = getDefaultApexState();

const apexSlice = createSlice({
  name: 'apex',
  initialState,
  reducers: {
    resetApexState: () => getDefaultApexState(),
    markApexLoaded: (
      state,
      action: PayloadAction<{ userId: string; accountIds: string[]; at: number }>,
    ) => {
      state.cache.userId = action.payload.userId;
      state.cache.accountIds = action.payload.accountIds;
      state.cache.loadedAt = action.payload.at;
    },
    // updateAccounts: (state, action: PayloadAction<any>) => {
    //   state.globalApexData = action.payload;
    // },
    // setGlobalApexAssetMapData: (state, action: PayloadAction<any>) => {
    //   state.globalApexAssetMapData = action.payload;
    // },
    // updateUserInfo: (state, action: PayloadAction<Partial<ApexUserInformation>>) => {
    //   state.userInfo = { ...state.userInfo, ...action.payload };
    // },
    // resetUserInfo: (state) => {
    //   state.userInfo = {
    //     taxIdType: "TAX_ID_TYPE_SSN",
    //   };
    // },

    updateUserInfo: (state, action: PayloadAction<Partial<ApexUserInformation>>) => {
      state.userInfo = {
        ...state.userInfo,
        ...action.payload,
      };
    },
    setApexAccounts: (state, action: PayloadAction<ApexAccount[]>) => {
      state.accounts = action.payload.sort((a, b) => (a.type < b.type ? -1 : 1));
    },
    // set account detailed information
    // setAccountDetail: (
    //   state,
    //   action: PayloadAction<{ accountId: string; detail: ApexAccountDetail }>,
    // ) => {
    //   const { accountId, detail } = action.payload;
    //   state.accountDetails[accountId] = detail;
    // },
    // // set risk scores
    // setRiskScores: (state, action: PayloadAction<Record<string, number>>) => {
    //   state.riskScores = { ...state.riskScores, ...action.payload };
    // },
    // // set chart data
    // setChartData: (state, action: PayloadAction<ChartDataPoint[]>) => {
    //   state.chartData = action.payload;
    // },
    updateAssetMap: (state, action: PayloadAction<AssetMap>) => {
      state.assetMap = action.payload;
    },
    submitStart: (state) => {
      state.submissionState = { kind: 'pending' };
    },
    updateSubmissionState: (
      state,
      action: PayloadAction<{
        kind: 'failed' | 'success';
        message: string;
      }>,
    ) => {
      state.submissionState = action.payload;
    },
    signAgreementsStart: (state) => {
      state.submissionState = { kind: 'pending' };
    },
    refreshAccountState: (state) => {
      // handle the case when user has existing accounts; state intentionally unused
      void state;
    },
    // setLoading: (state, action: PayloadAction<boolean>) => {
    //   state.loading = action.payload;
    // },
  },
  // reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchApexAccounts.pending, (state) => {
        state.loading.accounts = true;
      })
      .addCase(fetchApexAccounts.fulfilled, (state, action) => {
        state.loading.accounts = false;
        const accounts = action.payload.filter((account) => !isClosedOrPrepareForClosure(account));
        state.accounts = accounts;
      })
      .addCase(fetchApexAccounts.rejected, (state, action) => {
        state.loading.accounts = false;
        if (action.meta.aborted || action.payload === APEX_STALE_USER_REQUEST) {
          return;
        }
        state.error = action.error.message || 'Failed to fetch accounts';
      })
      .addCase(fetchApexAssetMap.pending, (state) => {
        state.loading.assetMap = true;
      })
      .addCase(fetchApexAssetMap.fulfilled, (state, action) => {
        state.loading.assetMap = false;
        state.assetMap = action.payload;
      })
      .addCase(fetchApexAssetMap.rejected, (state, action) => {
        state.loading.assetMap = false;
        if (action.meta.aborted || action.payload === APEX_STALE_USER_REQUEST) {
          return;
        }
        state.error = action.error.message || 'Failed to fetch asset map';
      })
      .addCase(fetchAccountAllocation.pending, (state) => {
        state.loading.allocation = true;
      })
      .addCase(fetchAccountAllocation.fulfilled, (state, action) => {
        state.loading.allocation = false;
        const { accountId, data } = action.payload;
        const account = state.accounts.find((acc) => acc.id === accountId);
        if (account) {
          account.allocation = data;
        }
      })
      .addCase(fetchAccountAllocation.rejected, (state, action) => {
        state.loading.allocation = false;
        if (action.meta.aborted || action.payload === APEX_STALE_USER_REQUEST) {
          return;
        }

        const { accountId } = action.meta.arg;
        const account = state.accounts.find((acc) => acc.id === accountId);
        if (account) {
          account.allocation = null;
        }
        state.error = action.error.message || 'Failed to fetch allocation data';
      })
      // .addCase(fetchAccountDetails.pending, (state) => {
      //   state.loading.details = true;
      // })
      // .addCase(fetchAccountDetails.fulfilled, (state, action) => {
      //   state.loading.details = false;
      //   const { accountId, data } = action.payload;
      //   const account = state.accounts.find((acc) => acc.id === accountId);
      //   if (account) {
      //     account.account_detail = data;
      //   }
      // })
      // .addCase(fetchAccountDetails.rejected, (state, action) => {
      //   state.loading.details = false;
      //   state.error =
      //     action.error.message || "Failed to fetch account details data";
      // })
      .addCase(fetchAccountFinancialDetails.pending, (state) => {
        state.loading.financialDetails = true;
      })
      .addCase(fetchAccountFinancialDetails.fulfilled, (state, action) => {
        state.loading.financialDetails = false;
        const { accountId, data } = action.payload;
        const account = state.accounts.find((acc) => acc.id === accountId);
        if (account) {
          account.cash_available_for_withdraw = data.cash_available_for_withdraw;
          account.ira_contribution_constraints = data.ira_contribution_constraints;
          account.ira_distribution_constraints = data.ira_distribution_constraints;
        }
      })
      .addCase(fetchAccountFinancialDetails.rejected, (state, action) => {
        state.loading.financialDetails = false;
        if (action.meta.aborted || action.payload === APEX_STALE_USER_REQUEST) {
          return;
        }
        state.error = action.error.message || 'Failed to fetch financial details data';
      })
      .addCase(fetchApexChartData.pending, (state) => {
        state.loading.chart = true;
      })
      .addCase(fetchApexChartData.fulfilled, (state, action) => {
        state.loading.chart = false;
        state.chartData = action.payload.chartData;
        state.cache.range = action.meta.arg.range ?? null;
      })
      .addCase(invalidateWealthData, (state) => {
        state.cache = { ...emptyWealthDataCache(), accountIds: [] };
      })
      .addCase(fetchApexChartData.rejected, (state, action) => {
        state.loading.chart = false;
        if (action.meta.aborted || action.payload === APEX_STALE_USER_REQUEST) {
          return;
        }
        state.error = action.error.message || 'Failed to fetch chart data';
      })
      .addCase(fetchAccountHoldings.pending, (state, action) => {
        state.loading.holdings = true;
        delete state.holdingsFailedById[action.meta.arg];
      })
      .addCase(fetchAccountHoldings.fulfilled, (state, action) => {
        state.loading.holdings = false;
        const { accountId, data } = action.payload;
        const holdings = withUnitPrices(data?.holdings);
        // Always record by id, independent of whether a legacy `accounts`
        // entry exists — this is what firm-data-blended custodian accounts
        // (looked up by custodian_account_id) read from.
        state.custodianHoldingsById[accountId] = holdings;
        const account = state.accounts.find((acc) => acc.id === accountId);
        if (account) {
          account.holdings = holdings;
        }
      })
      .addCase(fetchAccountHoldings.rejected, (state, action) => {
        state.loading.holdings = false;
        if (action.meta.aborted || action.payload === APEX_STALE_USER_REQUEST) {
          return;
        }
        state.error = action.error.message || 'Failed to fetch holdings data';
        // Settle the account on an empty list so the panel stops loading.
        const accountId = action.meta.arg;
        state.holdingsFailedById[accountId] = true;
        state.custodianHoldingsById[accountId] = [];
        const account = state.accounts.find((acc) => acc.id === accountId);
        if (account) {
          account.holdings = [];
        }
      })
      .addCase(fetchAccountTransfers.pending, (state) => {
        state.loading.transfers = true;
      })
      .addCase(fetchAccountTransfers.fulfilled, (state, action) => {
        state.loading.transfers = false;
        const { accountId, data } = action.payload;
        // Always record by id, independent of whether a legacy `accounts`
        // entry exists -- this is what firm-data-blended custodian accounts
        // (looked up by custodian_account_id) read from.
        state.custodianTransfersById[accountId] = data;
        const account = state.accounts.find((acc) => acc.id === accountId);
        if (account) {
          account.transferList = data;
        }
      })
      .addCase(fetchAccountTransfers.rejected, (state, action) => {
        state.loading.transfers = false;
        if (action.meta.aborted || action.payload === APEX_STALE_USER_REQUEST) {
          return;
        }
        state.error = action.error.message || 'Failed to fetch transfers data';
      });
  },
});

export function phrase1Complete(userInfo: ApexUserInformation): boolean {
  const hasRequiredPersonalInfo =
    userInfo.firstName !== undefined &&
    userInfo.firstName.length > 0 &&
    userInfo.lastName !== undefined &&
    userInfo.lastName.length > 0 &&
    userInfo.suffix !== undefined &&
    userInfo.phoneNumber !== undefined &&
    userInfo.phoneNumber.length > 0;

  const hasRequiredAddress =
    userInfo.personalAddress !== undefined &&
    userInfo.personalAddress.line1.length > 0 &&
    userInfo.personalAddress.city.length > 0 &&
    userInfo.personalAddress.state.length > 0 &&
    userInfo.personalAddress.postalCode.length === 5;

  return hasRequiredPersonalInfo && hasRequiredAddress;
}

export function phrase2Complete(userInfo: ApexUserInformation): boolean {
  // const hasEmploymentDetails =
  //   userInfo.employmentStatus === "EMPLOYED"
  //     ? userInfo.occupation !== undefined
  //     : true;
  const hasEmploymentDetails =
    userInfo.employmentStatus === 'EMPLOYED' || userInfo.employmentStatus === 'SELF_EMPLOYED'
      ? userInfo.occupation !== undefined && userInfo.occupation.trim().length > 0
      : true;

  // validate special characters
  const hasValidOccupation = userInfo.occupation
    ? validateOccupation(userInfo.occupation) === undefined
    : true;

  const hasValidEmployer = userInfo.employer
    ? validateEmployer(userInfo.employer) === undefined
    : true;

  // return userInfo.employmentStatus !== undefined && hasEmploymentDetails;

  return (
    userInfo.employmentStatus !== undefined &&
    hasEmploymentDetails &&
    hasValidOccupation &&
    hasValidEmployer
  );
}

export function phrase3Complete(userInfo: ApexUserInformation): boolean {
  return userInfo.taxId?.length === 9;
}

export function phrase4Complete(userInfo: ApexUserInformation): boolean {
  return (
    userInfo.annualIncomeRangeUsd !== undefined && userInfo.totalNetWorthRangeUsd !== undefined
  );
}

export function phrase5Complete(userInfo: ApexUserInformation): boolean {
  // Check large trader requirements
  if (userInfo.isLargeTrader) {
    if (!userInfo.largeTrader?.id || !userInfo.largeTrader?.effectiveDate) {
      return false;
    }
  }

  // Check trusted contact requirements if provided
  if (userInfo.trustedContacts && userInfo.trustedContacts.length > 0) {
    const trustedContact = userInfo.trustedContacts[0];

    // If any trusted contact field is filled, validate the entire contact
    const hasAnyTrustedContactData =
      trustedContact.givenName ||
      trustedContact.familyName ||
      trustedContact.phoneNumber ||
      trustedContact.email ||
      trustedContact.mailingAddress?.line1;

    if (hasAnyTrustedContactData) {
      // Use the comprehensive validation function
      return isValidTrustedContact(trustedContact);
    }
  }
  return true;
}

export const {
  resetApexState,
  markApexLoaded,
  updateUserInfo,
  setApexAccounts,
  // setAccountDetail,
  // setRiskScores,
  // setChartData,
  updateAssetMap,
  submitStart,
  updateSubmissionState,
  signAgreementsStart,
  refreshAccountState,
  // setLoading,
} = apexSlice.actions;

export default apexSlice.reducer;

function isClosedOrPrepareForClosure(account: ApexAccount) {
  return isClosedOrClosingAccount(account.account_detail);
}
